/**
 * RecordCard — the house card grammar (`record.html` `.card`/`.card-h`/`.card-b`;
 * WEB TIER A · WT4).
 *
 * One surface vocabulary for every card on a record page: `bg/surface`, a 1px
 * `border/default` hairline (decoration — it separates two surfaces and
 * removing it removes nothing, so it keeps `border-line` rather than the
 * control-rim token), `radius/md`, and an optional header row of `H3/16`
 * title + `Label/12` quiet sub. `overflow-hidden` so flush children (tables,
 * step lists, band heads) meet the card edge cleanly.
 *
 * The existing `Card` primitive (`Card.tsx` — the dashboard widget shell)
 * survives untouched on its own consumers; this is the RECORD grammar the
 * approved mockup draws — header-with-sub, flush bodies, 52px header row —
 * which `Card` does not speak. Two components, two Figma/mockup provenances.
 *
 * THE TITLE IS A HEADING. The mockup draws `card-t` as a styled span because a
 * static mockup has no AT contract; the product page does — every card title
 * here is an `h2`, which is what gives the record its outline (identity →
 * lifecycle → lines → aside) to a screen-reader user.
 */

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface RecordCardProps {
  /** A string renders as the `h2` card title; pass a node to own the markup. */
  title?: ReactNode;
  /** The quiet right half of the header row (`card-s`) — a fact, not a control. */
  sub?: ReactNode;
  /** A control on the header row's right (rendered after `sub` when both exist). */
  headerAction?: ReactNode;
  /**
   * Flush children meet the card edges (tables, step lists); the default body
   * is the mockup's `card-b` (16px padding, 14px stack gap).
   */
  flush?: boolean;
  children: ReactNode;
  className?: string;
}

export function RecordCard({
  title,
  sub,
  headerAction,
  flush = false,
  children,
  className,
}: RecordCardProps): React.JSX.Element {
  const hasHeader = title !== undefined || sub !== undefined || headerAction !== undefined;
  return (
    <section
      className={cn('overflow-hidden rounded-md border border-line bg-surface', className)}
    >
      {hasHeader ? (
        <header className="flex min-h-[52px] items-center justify-between gap-4 border-b border-line px-4 py-3">
          {typeof title === 'string' ? <h2 className="text-h3 text-fg">{title}</h2> : title}
          <div className="flex min-w-0 items-center gap-3">
            {sub === undefined ? null : (
              <span className="text-label text-fg-secondary">{sub}</span>
            )}
            {headerAction}
          </div>
        </header>
      ) : null}
      {flush ? children : <div className="flex flex-col gap-3.5 p-4">{children}</div>}
    </section>
  );
}
